import {takeEvery} from 'redux-saga';
import {call, put} from 'redux-saga/effects';
import {getUser} from './Authenticate';
import {replaceInArray, mergeInArray} from '../lib/helpers';
import {appError, busy, ready} from './Status';

const meta = {requireAuthentication: true};

const SHOW_ADD_COMMENT = 'app-qa/question/SHOW_ADD_COMMENT';
export const addComment = (parentType, parentId, showAddComment) =>
  ({type: SHOW_ADD_COMMENT, parentType, parentId, showAddComment, meta});

const SHOW_ADD_COMMENT_SUCCESS = 'app-qa/question/SHOW_ADD_COMMENT_SUCCESS';
export const addCommentSuccess = (parentType, parentId, showAddComment) =>
    ({type: SHOW_ADD_COMMENT_SUCCESS, parentType, parentId, showAddComment});

const CREATE_COMMENT = 'app-qa/question/CREATE_COMMENT';
export const createComment = ({comment}) =>
  ({type: CREATE_COMMENT, comment, meta});

const CREATE_COMMENT_SUCCESS = 'app-qa/question/CREATE_COMMENT_SUCCESS';
export const createCommentSuccess = (parentType, parentId, comments) => ({type: CREATE_COMMENT_SUCCESS, parentType, parentId, comments});

const SHOW_ALL_COMMENTS = 'app-qa/question/SHOW_ALL_COMMENTS';
export const showAllComments = ({parentType, parentId, showComments}) =>
  ({type: SHOW_ALL_COMMENTS, parentType, parentId, showComments});

const EDIT_COMMENT = 'app-qa/question/EDIT_COMMENT';
export const editComment = (parentType, parentId, commentId, editing) =>
  ({type: EDIT_COMMENT, parentType, parentId, commentId, editing});

const UPDATE_COMMENT = 'app-qa/question/UPDATE_COMMENT';
export const updateComment = (parentType, comment) =>
  ({type: UPDATE_COMMENT, parentType, comment});

const UPDATE_COMMENT_SUCCESS = 'app-qa/question/UPDATE_COMMENT_SUCCESS';
export const updateCommentSuccess = (parentType, comment) => ({type: UPDATE_COMMENT_SUCCESS, parentType, comment});

const MODERATE_COMMENT = 'app-qa/question/MODERATE_COMMENT';
export const moderateComment = (parentType, comment) => ({type: MODERATE_COMMENT, parentType, comment});

export const actions = {
  SHOW_ADD_COMMENT,
  SHOW_ADD_COMMENT_SUCCESS,
  CREATE_COMMENT,
  CREATE_COMMENT_SUCCESS,
  EDIT_COMMENT,
  UPDATE_COMMENT,
  UPDATE_COMMENT_SUCCESS,
  MODERATE_COMMENT,
  SHOW_ALL_COMMENTS,
};

const updateQuestionComments = (state, updatedComments) => {
  const questionUpdate = {...state.currentQuestion, comments: updatedComments};
  return {...state, currentQuestion: questionUpdate};
};

const updateAnswers = (state, updatedAnswers) => {
  const questionUpdate = {...state.currentQuestion, answers: updatedAnswers};
  return {...state, currentQuestion: questionUpdate};
};

const getAnswer = (state, id) => state.currentQuestion.answers.filter((a) => a.id === id)[0];

export default function reducer(state = {}, action = {}) {
  switch (action.type) {
    case SHOW_ADD_COMMENT_SUCCESS: {
      if (action.parentType === 'question') {
        const questionUpdate = Object.assign({}, state.currentQuestion, {showAddComment: action.showAddComment});
        return Object.assign({}, state, {currentQuestion: questionUpdate});
      }
      // Else its an answer
      const modifiedAnswer = {id: action.parentId, showAddComment: action.showAddComment};
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, modifiedAnswer);
      return updateAnswers(state, updatedAnswers);
    }
    case CREATE_COMMENT_SUCCESS: {
      if (action.parentType === 'question') {
        return updateQuestionComments(state, action.comments);
      }
      const answer = getAnswer(state, action.parentId);
      answer.comments = action.comments;
      const updatedAnswers = replaceInArray(state.currentQuestion.answers, answer);
      return updateAnswers(state, updatedAnswers);
    }
    case EDIT_COMMENT: {
      if (action.parentType === 'question') {
        const commentUpdate = {id: action.commentId, editing: action.editing};
        const updatedComments = mergeInArray(state.currentQuestion.comments, commentUpdate);
        return updateQuestionComments(state, updatedComments);
      }
      const answer = getAnswer(state, action.parentId);
      const commentUpdate = {id: action.commentId, editing: action.editing};
      const updatedComments = mergeInArray(answer.comments, commentUpdate);
      answer.comments = updatedComments;
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, answer);
      return updateAnswers(state, updatedAnswers);
    }
    case UPDATE_COMMENT_SUCCESS: {
      if (action.parentType === 'question') {
        const updatedComments = replaceInArray(state.currentQuestion.comments, action.comment);
        return updateQuestionComments(state, updatedComments);
      }
      const answer = getAnswer(state, action.comment.answer_id);
      const updatedComments = replaceInArray(answer.comments, action.comment);
      answer.comments = updatedComments;
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, answer);
      return updateAnswers(state, updatedAnswers);
    }
    case SHOW_ALL_COMMENTS : {
      if (action.parentType === 'question') {
        const questionUpdate = Object.assign({}, state.currentQuestion, {showComments: action.showComments});
        return Object.assign({}, state, {currentQuestion: questionUpdate});
      }
      // Else its an answer
      const modifiedAnswer = {id: action.parentId, showComments: action.showComments};
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, modifiedAnswer);
      return updateAnswers(state, updatedAnswers);
    }
    default:
      return state;
  }
}

export function sagas({qaClient}) {
  function *showAddCommentSaga({parentType, parentId, showAddComment}) {
    yield put(busy({blockUi: true}));
    try {
      yield put(getUser());
      yield put(addCommentSuccess(parentType, parentId, showAddComment));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *createCommentSaga({comment}) {
    yield put(busy({blockUi: true}));
    try {
      const commentResult = yield call(() => qaClient.createComment(comment));
      const parentType = comment.answer_id !== null ? 'answer' : 'question';
      const parentId = comment.answer_id !== null ? comment.answer_id : comment.question_id;
      yield put(createCommentSuccess(parentType, parentId, commentResult.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *updateCommentSaga({parentType, comment}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.updateComment(comment));
      yield put(updateCommentSuccess(parentType, response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *moderateCommentSaga({parentType, comment}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.moderateComment(comment));
      yield put(updateCommentSuccess(parentType, response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  return {
    *watchAddComment() { yield* takeEvery(SHOW_ADD_COMMENT, showAddCommentSaga); },
    *watchCreateComment() { yield* takeEvery(CREATE_COMMENT, createCommentSaga); },
    *watchUpdateComment() { yield* takeEvery(UPDATE_COMMENT, updateCommentSaga); },
    *watchModerateComment() { yield* takeEvery(MODERATE_COMMENT, moderateCommentSaga); },
  };
}
